import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccess() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('polling'); // polling | paid | failed | expired
  const [message, setMessage] = useState('Confirming your payment with Stripe...');
  const [tier, setTier] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState('failed');
      setMessage('No checkout session found. Returning to settings...');
      setTimeout(() => navigate('/settings'), 2500);
      return;
    }

    let timer = null;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api().get(`/api/billing/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setState('paid');
          setTier(data.tier);
          setMessage('Subscription activated. Welcome aboard!');
          setTimeout(() => navigate('/dashboard'), 2500);
          return;
        }
        if (data.status === 'expired') {
          setState('expired');
          setMessage('Checkout session expired. Please try again.');
          setTimeout(() => navigate('/settings'), 3000);
          return;
        }
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setState('failed');
          setMessage('Still processing. Check your email for confirmation, or refresh this page.');
          return;
        }
        setMessage(`Processing payment... (${attemptsRef.current}/${MAX_POLL_ATTEMPTS})`);
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        setState('failed');
        setMessage('Could not verify payment. Please contact support if you were charged.');
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [api, navigate]);

  const icon = state === 'paid'
    ? <CheckCircle2 className="text-emerald-500" size={56} />
    : state === 'polling'
      ? <Loader2 className="text-slate-700 animate-spin" size={56} />
      : <XCircle className="text-red-500" size={56} />;

  return (
    <div
      data-testid="billing-success-page"
      className="min-h-screen bg-slate-50 flex items-center justify-center px-6"
    >
      <div className="bg-white max-w-md w-full rounded-2xl border border-slate-200 p-10 shadow-sm text-center">
        <div className="flex justify-center mb-6">{icon}</div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          {state === 'paid' ? 'Payment Successful' : state === 'polling' ? 'Confirming Payment' : 'Payment Issue'}
        </h1>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">{message}</p>
        {tier && (
          <p className="text-xs text-slate-400 mt-4 uppercase tracking-widest">Plan: {tier.replace(/_/g, ' ')}</p>
        )}
        {state !== 'polling' && (
          <button
            data-testid="billing-success-back"
            onClick={() => navigate(state === 'paid' ? '/dashboard' : '/settings')}
            className="mt-8 inline-flex items-center bg-slate-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all"
          >
            {state === 'paid' ? 'Go to Dashboard' : 'Back to Settings'}
          </button>
        )}
      </div>
    </div>
  );
}
